<script lang="ts">
  import type ArchieML from '$locales/en/content.json';

  interface Props {
    /* Whether the app is being rendered in an embed */
    embedded?: boolean;
    /* ArchieML story content */
    content: (typeof ArchieML)['story'];
  }

  let { embedded = false, content }: Props = $props();

  import { assets } from '$app/paths';

  import {
    Article,
    Analytics,
    BodyText,
    EndNotes,
    SiteHeadline,
    Headline,
    GraphicBlock,
    InlineAd,
    Video,
    PhotoPack,
    ScrollerVideo,
    ScrollerVideoForeground,
    Block,
  } from '@reuters-graphics/graphics-components';
  import LogBlock from './components/dev/LogBlock.svelte';
  import { containerWidth, inlineAdNumber } from '$utils/propValidators';
  import { isReutersDotcom } from '$utils/env';
  import { page } from '$app/state';
  import pkg from '$pkg';
  import { truthy } from '$utils/propValidators';
  import SvelteFigures from './components/SvelteFigures.svelte';
  import SvelteFigures1 from './components/SvelteFigures1.svelte';
  import SvelteFigures2 from './components/SvelteFigures2.svelte';
  import SvelteFigures3 from './components/SvelteFigures3.svelte';
  import VideoGrid from './components/VideoGrid.svelte';

  // Import ai2svelte components...
  import AiMap from './ai2svelte/ai-chart.svelte';
  import Rankings from './ai2svelte/1972-skate-rankings.svelte';
  import Percentages from './ai2svelte/skate-percentages.svelte';
  import Quads from './ai2svelte/quads.svelte';
  import Figures from './ai2svelte/figure-patterns.svelte';
  import Comparison from './ai2svelte/comparison.svelte';
  import TopScroller from './components/topScroller.svelte';
  import IceSkateBlade from './ai2svelte/ice-skate-Blade.svelte';

  // ...and add them to this object.
  const aiCharts = {
    AiMap,
    Figures,
    Rankings,
    Percentages,
    IceSkateBlade,
    Quads,
    Comparison,
  };
</script>

{#if !embedded && isReutersDotcom(page.url)}
  <Analytics authors={pkg?.reuters?.graphic?.authors || []} />
{/if}

<Article>
  <!--
    This component and others are part of our components library.
    📚 Read the docs: https://reuters-graphics.github.io/graphics-components/
  -->

  <!-- 🔁 Looping through your ArchieML doc blocks... -->
  {#each content.blocks as block}
    {#if block.type === 'scroller-video'}
      <Block width="fluid">
        <TopScroller {content} {embedded} />
      </Block>
    {:else if block.type === 'video'}
      <Video
        ariaDescription={block.ariaDescription}
        src={`${assets}/${block.src}`}
        width={containerWidth(block.width)}
        loopVideo={truthy(block.loopVideo)}
        notes={block.notes}
      />
    {:else if block.type === 'text'}
      <BodyText text={block.text} />
    {:else if block.type === 'hed'}
      <Headline
        hed={content.hed}
        dek={content.dek}
        authors={content.authors}
        class="headlineTop"
        publishTime={content.publishTime}
        updateTime={content.updateTime}
      />
    {:else if block.type === 'sveltefigures'}
      <GraphicBlock
        id={block.id || 'figure-skating-shapes'}
        width={containerWidth(block.width)}
        textWidth={containerWidth(block.textWidth)}
        title={block.title}
        description={block.description}
        notes={block.notes}
        ariaDescription={block.altText}
      >
        {#if block.id === 'figure-skating-shapes-1'}
          <SvelteFigures1
            subhead={block.subhead}
            figures={block.figures}
            legendText={block.legendText}
          />
        {:else if block.id === 'figure-skating-shapes-2'}
          <SvelteFigures2 figures={block.figures} />
        {:else if block.id === 'figure-skating-shapes-3'}
          <SvelteFigures3 figures={block.figures} />
        {:else}
          <SvelteFigures
            subhead={block.subhead}
            figures={block.figures}
            legendText={block.legendText}
          />
        {/if}
      </GraphicBlock>
    {:else if block.type === 'video-grid'}
      <VideoGrid videos={block.videos} />
    {:else if block.type === 'photo-pack'}
      <!-- Pass `assets` into the image source in graphics kit -->
      <PhotoPack
        id={block.id}
        class={block.class}
        width={block.width}
        textWidth={block.textWidth}
        gap={Number(block.gap)}
        images={block.images.map((img) => ({
          ...img,
          src: `${assets}/${img.src}`,
          maxHeight: 659,
        }))}
        layouts={[
          { breakpoint: 750, rows: [2] },
          { breakpoint: 450, rows: [1, 1] },
        ]}
      />
      <!-- Ai2svelte graphic block -->
    {:else if block.type === 'ai-graphic'}
      {#if !aiCharts[block.chart]}
        <LogBlock message={`Unable to find "${block.chart}" in aiCharts`} />
      {:else}
        {@const AiChart = aiCharts[block.chart]}
        <GraphicBlock
          id={block.chart}
          width={containerWidth(block.width)}
          textWidth={containerWidth(block.textWidth)}
          title={block.title}
          description={block.description}
          notes={block.notes}
          ariaDescription={block.altText}
        >
          <AiChart assetsPath={assets || '/'} />
        </GraphicBlock>
      {/if}

      <!-- Inline ad -->
    {:else if block.type === 'inline-ad'}
      {#if isReutersDotcom(page.url)}
        <InlineAd n={inlineAdNumber(block.n)} />
      {:else}
        <LogBlock level="info" message="An ad will appear here on dotcom" />
      {/if}

      <!-- Warning block -->
    {:else}
      <LogBlock message={`Unknown block type: "${block.type}"`} />
    {/if}
  {/each}

  <EndNotes notes={content.endNotes} />
</Article>
